import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { themeColors } from '@hhgtech/hhg-components/mantine'
import Cookies from 'js-cookie'
import inRange from 'lodash/inRange'
import queryString from 'query-string'
import { IntlFormatters } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import * as BmiFormHelpers from 'components/molecules/bmiForm/index.helpers'
import { LOCALE_SPECS } from 'interfaces/constants'
import { Category, HealthToolPositionType, LOCALE } from 'interfaces/types'
import { formatGenderUrl } from 'utils/format'

import { BMI_ADDITIONAL_QUESTIONS_BY_NAME } from './constants'

export const BMI_SIDIS_KEY = 'bmis'
export const bmiLevelsTemplate = {
  underweight: {
    id: 'bmi.result.underweight',
    defaultMessage: 'Underweight',
    color: themeColors.blue[6],
    score: 1,
    male: {
      inactiveImage: '/images/bmi-underweight-male-inactive.svg',
      activeImage: '/images/bmi-underweight-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-underweight-female-inactive.svg',
      activeImage: '/images/bmi-underweight-female-active.svg',
    },
  },
  healthy: {
    id: 'bmi.result.healthy',
    defaultMessage: 'Healthy',
    color: themeColors.green[6],
    score: 2,
    male: {
      inactiveImage: '/images/bmi-healthy-male-inactive.svg',
      activeImage: '/images/bmi-healthy-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-healthy-female-inactive.svg',
      activeImage: '/images/bmi-healthy-female-active.svg',
    },
  },
  overweight: {
    id: 'bmi.result.overweight',
    defaultMessage: 'Overweight',
    color: themeColors.yellow[6],
    score: 3,
    male: {
      inactiveImage: '/images/bmi-overweight-male-inactive.svg',
      activeImage: '/images/bmi-overweight-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-overweight-female-inactive.svg',
      activeImage: '/images/bmi-overweight-female-active.svg',
    },
  },
  obese_level: {
    id: 'bmi.result.obese',
    defaultMessage: 'Obese',
    color: themeColors.yellow[9],
    score: 4,
    male: {
      inactiveImage: '/images/bmi-obese_level_1-male-inactive.svg',
      activeImage: '/images/bmi-obese_level_1-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-obese_level_1-female-inactive.svg',
      activeImage: '/images/bmi-obese_level_1-female-active.svg',
    },
  },
  obese_level_1: {
    id: 'bmi.result.obese1',
    defaultMessage: 'Obese 1',
    color: themeColors.yellow[9],
    score: 4,
    male: {
      inactiveImage: '/images/bmi-obese_level_1-male-inactive.svg',
      activeImage: '/images/bmi-obese_level_1-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-obese_level_1-female-inactive.svg',
      activeImage: '/images/bmi-obese_level_1-female-active.svg',
    },
  },
  obese_level_2: {
    id: 'bmi.result.obese2',
    defaultMessage: 'Obese 2',
    color: themeColors.red[8],
    score: 5,
    male: {
      inactiveImage: '/images/bmi-obese_level_2-male-inactive.svg',
      activeImage: '/images/bmi-obese_level_2-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-obese_level_2-female-inactive.svg',
      activeImage: '/images/bmi-obese_level_2-female-active.svg',
    },
  },
  obese_level_3: {
    id: 'bmi.result.obese3',
    defaultMessage: 'Obese 3',
    color: themeColors.red[9],
    score: 6,
    male: {
      inactiveImage: '/images/bmi-obese_level_3-male-inactive.svg',
      activeImage: '/images/bmi-obese_level_3-male-active.svg',
    },
    female: {
      inactiveImage: '/images/bmi-obese_level_3-female-inactive.svg',
      activeImage: '/images/bmi-obese_level_3-female-active.svg',
    },
  },
}
export type BmiLevelKeys = keyof typeof bmiLevelsTemplate
let bmiConditionsValueBase: Partial<Record<BmiLevelKeys, [number, number]>> = {
  underweight: [0, 18.5],
  healthy: [18.5, 23],
  overweight: [23, 25],
  obese_level_1: [25, 30],
  obese_level_2: [30, 999],
}
// type BmiConditionValue = typeof bmiConditionsValueBase
export type BmiConditionLevel = {
  isActive: boolean
  value: number
  valueKey: string
  score: number
  image: string
  title: string
  description: string
  color: string
  percent: number
  milestone: string
}

const updateRange = (bmiRange?: any) => {
  if (!bmiRange) return
  bmiConditionsValueBase = {
    underweight: [0, bmiRange.underweight],
    healthy: [bmiRange.underweight, bmiRange.healthy],
    overweight: [bmiRange.healthy, bmiRange.overweight],
    obese_level_1: [bmiRange.overweight, bmiRange.obese_level_1],
    obese_level_2: [bmiRange.obese_level_1, bmiRange.obese_level_2 || 999],
    ...(bmiRange.obese_level_2
      ? {
          obese_level_3: [
            bmiRange.obese_level_2,
            bmiRange.obese_level_3 || 999,
          ],
          // ...(bmiRange.obese_level_3
          //   ? {
          //       obese_level_4: [bmiRange.obese_level_3, 999],
          //     }
          //   : {}),
        }
      : {}),
  }
  if (
    !bmiRange?.obese_level_1 &&
    !bmiRange?.obese_level_2 &&
    !bmiRange?.obese_level_3
  ) {
    bmiConditionsValueBase.obese_level = [bmiRange.overweight, 999]
    delete bmiConditionsValueBase.obese_level_1
    delete bmiConditionsValueBase.obese_level_2
    delete bmiConditionsValueBase.obese_level_3
    return
  }
}

export function getBmiLevel(bmi?: number, locale?: LOCALE, bmiRange?: any) {
  if (!bmi || !locale) {
    return 1
  }
  let level = 1
  updateRange(bmiRange)
  const bmiConditions = bmiConditionsValueBase
  Object.keys(bmiConditions).forEach((k) => {
    const key = k as BmiLevelKeys
    const condValue = bmiConditions[key]
    // console.log(key)

    if (condValue && inRange(bmi, condValue[0], condValue[1])) {
      level = bmiLevelsTemplate[key].score
    }
  })
  return level
}

export function generateBmiLevel({
  f,
  gender,
  bmiValue,
  bmiRange,
}: {
  f: IntlFormatters['formatMessage']
  gender: string
  bmiValue?: number
  bmiRange?: any
}): { activePercent: number; conditions: BmiConditionLevel[] } {
  updateRange(bmiRange)

  const conditions = bmiConditionsValueBase
  const conditionKeys = Object.keys(conditions)
  const conditionValues = Object.values(conditions)
  const conditionRangeValue =
    conditionValues[conditionValues.length - 1][0] - conditionValues[0][0] + 5
  const activePercent =
    typeof bmiValue !== 'undefined' ? (bmiValue / conditionRangeValue) * 100 : 0
  const tmpSum: number[] = []

  return {
    activePercent: Math.floor(activePercent),
    conditions: conditionKeys
      .map((k, index) => {
        const key = k as BmiLevelKeys
        const condValue = conditions[key]
        const bmiLevelTemplate = bmiLevelsTemplate[key]
        const isEndRange = Boolean(index === conditionKeys.length - 1)
        if (!condValue) {
          return
        }

        const startNumber = condValue[0].toString()
        const endNumber = condValue[1].toString()
        const sep = ' - '
        let description
        if (isEndRange) {
          description = '> ' + startNumber
        } else if (startNumber === '0') {
          /* `(+endNumber * 100 - 10) / 100` is calculating the upper limit of the
          BMI range for a given condition level, and formatting it to display
          as a string with one decimal place.
          FIX: Number("26.9")-0.1 = 26.799999999999997
          */
          // description = '< ' + (+endNumber * 100 - 10) / 100
          description = '< ' + endNumber
        } else {
          description = `${startNumber}${sep}${(+endNumber * 100 - 10) / 100}`
        }

        const tmp: BmiConditionLevel = {
          isActive: false,
          value: condValue[0],
          valueKey: key,
          score: bmiLevelTemplate?.score,
          image: '',
          title: f({
            id: bmiLevelTemplate.id,
            defaultMessage: bmiLevelTemplate.defaultMessage,
          }),
          description,
          color: bmiLevelTemplate.color,
          percent: 0,
          milestone: startNumber.toString() + (isEndRange ? '+' : ''),
        }

        tmp.isActive =
          typeof bmiValue !== 'undefined'
            ? condValue[0] <= bmiValue && bmiValue < condValue[1] // [min, max)
            : false

        if (gender === 'male' || gender === 'female') {
          if (tmp.isActive) {
            tmp.image = bmiLevelTemplate[gender].activeImage
          } else {
            tmp.image = bmiLevelTemplate[gender].inactiveImage
          }
        }

        // Find percent each condition
        let percent =
          Math.ceil(
            ((condValue[1] - condValue[0]) / conditionRangeValue) * 100 * 100,
          ) / 100
        if (index < conditionKeys.length - 1) {
          tmpSum[index] = percent
        }
        if (isEndRange) {
          percent =
            Math.floor((100 - tmpSum.reduce((s, n) => (s += n), 0)) * 100) / 100
        }
        tmp.percent = percent

        return tmp
      })
      .filter(Boolean) as BmiConditionLevel[],
  }
}

export function generateBmiRedirect(
  baseUrl: string,
  value: BmiFormHelpers.BmiFormValue,
): string {
  const redirectUrl = queryString.stringifyUrl(
    {
      url: baseUrl,
      query: {
        // [QUERY_HEALTH_TOOL.bmi]: value.bmi,
        [QUERY_HEALTH_TOOL.gender]: formatGenderUrl(value.gender as string),
        [QUERY_HEALTH_TOOL.age]: value.age,
        [QUERY_HEALTH_TOOL.height]: value.height,
        [QUERY_HEALTH_TOOL.weight]: value.weight,
        joincommunity: 1,
        [QUERY_HEALTH_TOOL.yourself]: Number(Boolean(value.isCheckYourself)),
        [QUERY_HEALTH_TOOL.activityLevel]: value.activityLevel,
      },
    },
    {
      skipEmptyString: true,
      skipNull: true,
    },
  )

  return redirectUrl
}

export function addMoreDataBMIAndSendToSidis(
  data: BmiFormHelpers.BmiFormValue,
  {
    articleCategories,
    // isMobile,
    positionType,
    additionalQuestion,
  }: {
    articleCategories?: Category[] | null
    isMobile: boolean
    positionType: HealthToolPositionType
    additionalQuestion?: BmiFormHelpers.AdditionalQuestionItem[]
  },
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataToSidis: any = {
    ...data,
    source_url: window.location.href,
    height: parseFloat(data.height || ''),
    weight: parseFloat(data.weight || ''),
    // device_type: isMobile ? 'mobile' : 'web',
    device_type: 'web',
    ...(data?.isCheckYourself && {
      is_my_self: 1,
    }),
    ...(Array.isArray(additionalQuestion) &&
      additionalQuestion.length > 0 && {
        additional_questions: additionalQuestion,
      }),
  }

  if (typeof dataToSidis.isCheckYourself !== 'undefined') {
    delete dataToSidis.isCheckYourself
  }

  if (articleCategories) {
    dataToSidis.categories = articleCategories.map((c) => ({
      name: c.name || '',
      permalink: c.permalink || '',
      term_id: c.termId || '',
      text_color: c.textColor || '',
    }))
  }

  if (positionType) {
    dataToSidis.position_type = positionType
  }

  return dataToSidis
}

export type PostBmiToSidisRequest = BmiFormHelpers.BmiFormValue & {
  account_id: number | null
  ga_client_id?: string
  site_id?: number
  email: string | null
  score_level?: number
  score?: number
  submited_at?: string
  categories?: Category[]
  device_type?: 'mobile' | 'web'
  position_type?: HealthToolPositionType
  is_my_self?: number
  active_level?: number
  additional_questions?: {
    question: {
      id: number
      value: string
    }
    answer: {
      id: number
      value: string
    }
  }[]
  source_url: string
}
export function postBmiToSidis(locale: LOCALE, data: PostBmiToSidisRequest) {
  const score_level = data?.score_level || getBmiLevel(data?.bmi, locale)
  const ga_client_id = data?.ga_client_id || Cookies.get('_ga') || ''
  const site_id = data?.site_id || parseInt(LOCALE_SPECS[locale].SITE_ID)
  const submited_at = data?.submited_at || new Date().toISOString()

  const sidisData = {
    age: Number(data?.age),
    gender: data?.gender === 'male',
    height: data?.height,
    weight: data?.weight,
    additional_questions: data?.additional_questions || [],
    objective: 1,
    account_id: data.account_id,
    ga_client_id,
    site_id,
    email: data.email,
    score_level,
    score: data?.bmi,
    submited_at,
    categories: data?.categories,
    position_type: data?.position_type,
    device_type: data?.device_type,
    is_my_self: data?.is_my_self,
    active_level: data?.active_level,
    url: window.location.href,
    source_url: data?.source_url,
  }
  return callApiNew(
    PATHS.SIDIS.POST_SIDIS_BMI,
    {
      method: 'POST',
      data: sidisData,
    },
    'sidis_update',
  )
}
export function getBmiAdditionalQNA(
  f: IntlFormatters['formatMessage'],
  formValues: BmiFormHelpers.BmiFormValue,
) {
  const questionKeys = ['question1', 'question2', 'newsletter'] as const
  const questionsMap = BMI_ADDITIONAL_QUESTIONS_BY_NAME(f)
  return questionKeys.map((key) => {
    const value = formValues[key as keyof BmiFormHelpers.BmiFormValue]
    return {
      question: questionsMap[key],
      answer: {
        id: questionsMap[key].id,
        value: value === '1' || value === true ? 'Yes' : 'No',
      },
    }
  })
}
